// Shopify-issued tracking tokens.
//
// Shopify stopped setting `_shopify_y`/`_shopify_s` itself, so we ask our
// backend for tokens it obtains by calling Shopify server-side (it reads
// `_y`/`_s`/`_cmp` off the Server-Timing header). We never mint IDs
// ourselves — an unrecognised token is worse than no token, since Shopify
// just discards analytics for IDs it didn't issue.

import { API_BASE_URL } from './bootstrap';
import { COOKIE_S, COOKIE_Y, LONG_TERM_SECONDS, SHORT_TERM_SECONDS, readShopifyCookies, writeCookie } from './cookies';

const FETCH_TIMEOUT_MS = 5_000;
// Shopify's placeholder value for "visitor hasn't consented yet" — never a
// usable token.
const NO_CONSENT_PREFIX = '00000000-';

export interface TrackingTokens {
	uniqueToken: string;
	visitToken: string;
}

function isUsable(token: string): boolean {
	return Boolean(token) && !token.startsWith(NO_CONSENT_PREFIX);
}

/**
 * Resolve the pair of Shopify-issued tracking tokens for this visitor.
 * Returns null when there's nothing usable to send — callers should treat
 * that as "skip dispatch this page load".
 */
export async function resolveTrackingTokens(
	cookieDomain: string,
	hasUserConsent: boolean,
): Promise<TrackingTokens | null> {
	if (!hasUserConsent) {
		writeCookie(COOKIE_Y, '', 0, cookieDomain);
		writeCookie(COOKIE_S, '', 0, cookieDomain);
		return null;
	}

	const existing = readShopifyCookies();
	if (existing.unique && existing.visit) {
		// Sliding session — extend _shopify_s on every page load.
		writeCookie(COOKIE_S, existing.visit, SHORT_TERM_SECONDS, cookieDomain);
		return { uniqueToken: existing.unique, visitToken: existing.visit };
	}

	const body: Record<string, string> = {};
	if (existing.unique) body.uniqueToken = existing.unique;
	if (existing.visit) body.visitToken = existing.visit;

	const controller = new AbortController();
	const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);

	let res: Response;
	try {
		res = await fetch(`${API_BASE_URL}/api/v1/tracking-values`, {
			method: 'POST',
			credentials: 'omit',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(body),
			signal: controller.signal,
		});
	} catch (err) {
		clearTimeout(timer);
		// eslint-disable-next-line no-console
		console.warn('[shopify-pixel] tracking-values request failed:', err);
		return null;
	}
	clearTimeout(timer);

	if (!res.ok) {
		// eslint-disable-next-line no-console
		console.warn(`[shopify-pixel] tracking-values ${res.status} ${res.statusText}`);
		return null;
	}

	const json = (await res.json()) as Record<string, unknown>;
	const data = (json.data ?? json) as { uniqueToken?: unknown; visitToken?: unknown };
	const uniqueToken = typeof data.uniqueToken === 'string' ? data.uniqueToken : '';
	const visitToken = typeof data.visitToken === 'string' ? data.visitToken : '';

	if (!isUsable(uniqueToken) || !isUsable(visitToken)) {
		// eslint-disable-next-line no-console
		console.warn('[shopify-pixel] tracking-values returned no usable tokens');
		return null;
	}

	writeCookie(COOKIE_Y, uniqueToken, LONG_TERM_SECONDS, cookieDomain);
	writeCookie(COOKIE_S, visitToken, SHORT_TERM_SECONDS, cookieDomain);

	return { uniqueToken, visitToken };
}
